/**
 * accountSelection.ts — the persisted account choice and the validation that
 * makes it safe to scope a query with.
 *
 * Shell: any (pure module, no React, no Supabase). Imported by `store.ts` and
 * `sessionBootstrap.ts`; deliberately depends on neither, so the dependency
 * runs one way and there is no cycle.
 *
 * ## Why this exists
 *
 * Before multi-seat, `currentAccountId` was *derived* from `auth.uid()` — one
 * user owned exactly one account, so there was nothing to choose and nothing to
 * validate. `account_members` changed that: a user can hold seats on several
 * accounts, so `currentAccountId` becomes a **user selection** that has to
 * survive a reload (docs/multi-seat-accounts.md §4.4).
 *
 * A persisted selection is a liability, not just a convenience. The id lands in
 * `.eq('account_id', currentAccountId)` on every staff query (CLAUDE.md
 * guardrail #1), so a value that is stale — the seat was revoked, the member was
 * soft-disabled, a different user signed in on this browser — is an id the app
 * would otherwise scope reads and writes by without anyone noticing. The RLS
 * policies would refuse it, but the failure mode is a silently empty app rather
 * than an obvious error, and the guardrail is meant to be belt *and* braces.
 *
 * ## The contract
 *
 * {@link resolveCurrentAccountId} is the only way a persisted id is allowed to
 * become `currentAccountId`, and it can only ever return an id **present in the
 * freshly-fetched membership list** (or `null` when that list is empty). A
 * stale id is therefore never scoped by — it is replaced, not trusted, and the
 * replacement is written back so the next load does not retry it.
 */

/**
 * localStorage key for the persisted account selection. Named in
 * docs/multi-seat-accounts.md §4.4.
 *
 * Deliberately NOT namespaced per user. The key survives sign-out so a
 * returning operator lands back on the account they were last using, and
 * {@link resolveCurrentAccountId} is what makes that safe: if the next user to
 * sign in on this browser is not an active member of that account, the id is
 * discarded on hydrate. Validation is the security boundary here, not the key
 * name.
 */
export const CURRENT_ACCOUNT_STORAGE_KEY = 'vendor.currentAccountId';

/** One account the signed-in user holds an active seat on. */
export interface AccountMembership {
  readonly id: string;
  readonly name: string;
  readonly role: AccountRole;
}

/**
 * Two roles only — owner and operator. There is no role ladder and no viewer;
 * adding one is a design note, not a union edit
 * (docs/multi-seat-accounts.md §6.1).
 */
export type AccountRole = 'owner' | 'operator';

/**
 * Read the persisted selection, or `null` when there is none.
 *
 * Never throws: Safari private mode and hardened browser profiles can make
 * `localStorage` access itself raise, and a storage quirk must not be able to
 * block sign-in.
 */
export function readPersistedAccountId(): string | null {
  try {
    const raw = localStorage.getItem(CURRENT_ACCOUNT_STORAGE_KEY);
    return raw != null && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Persist the selection, or remove it when `accountId` is `null`. Never throws,
 * for the same reason as {@link readPersistedAccountId}: losing the preference
 * is acceptable, failing the render is not.
 */
export function writePersistedAccountId(accountId: string | null): void {
  try {
    if (accountId == null) {
      localStorage.removeItem(CURRENT_ACCOUNT_STORAGE_KEY);
    } else {
      localStorage.setItem(CURRENT_ACCOUNT_STORAGE_KEY, accountId);
    }
  } catch {
    // Storage unavailable — the selection simply does not survive this reload.
  }
}

/**
 * Validate a persisted selection against the memberships just fetched from the
 * server, and return the id the session should actually scope by.
 *
 * The three outcomes, and why each is the right one:
 *
 * | `accounts`  | `persistedId`        | Result           |
 * | ----------- | -------------------- | ---------------- |
 * | empty       | anything             | `null`           |
 * | non-empty   | present in the list  | `persistedId`    |
 * | non-empty   | absent / `null`      | `accounts[0].id` |
 *
 * Falling back to the first entry rather than to `null` is deliberate
 * (§4.4): `null` would drop a perfectly good multi-seat user into the
 * account-less degraded branch, which reads as "your app is empty" rather than
 * "that account is gone". `null` is reserved for the one case that genuinely
 * means it — no active seat anywhere.
 *
 * `accounts` is expected to arrive in a stable order (the hydrate sorts by name
 * then id) so the fallback is deterministic rather than whatever PostgREST
 * happened to return first.
 */
export function resolveCurrentAccountId(
  accounts: readonly AccountMembership[],
  persistedId: string | null,
): string | null {
  const first = accounts[0];
  if (first == null) return null;
  if (persistedId != null && accounts.some((a) => a.id === persistedId)) return persistedId;
  return first.id;
}
